import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLButtonElement} [loadMoreButton] - Manual progressive-loading control.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {Map<number, Element[]>} */
  pageItems = new Map();

  /** @type {Map<number, Promise<void>>} */
  #pageRequests = new Map();

  /** @type {number} */
  #cacheGeneration = 0;

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {Element[]} */
  #nextPageItems = [];

  /** @type {{ page: number, url: URL } | null} */
  #nextPageInfo = null;

  /** @type {boolean} */
  #isRenderingNextPage = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #skeletonTimer;

  /** @type {boolean} */
  #replaceCardsWithSkeleton = false;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#observeViewMore();
    this.#updateLoadMoreButton();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#hideSkeleton();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                // Once the initial collection batch is rendered, prepare the
                // next page only when the customer approaches the control.
                // This keeps the initial route from downloading and parsing
                // another full product page in the background.
                if (this.#shouldPauseAutoLoading()) {
                  await this.#fetchPage('next');
                  this.#updateLoadMoreButton();
                } else {
                  this.#renderNextPage();
                }
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (error) {
      // Keep the current cards usable and allow a later retry. The customer
      // should never be trapped in a permanent loading state.
      console.warn('Mujerón: no se pudo cargar el siguiente grupo de productos.', error);
    } finally {
      // A failed or interrupted network request must never leave the list or
      // the load-more control waiting indefinitely.
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    if (this.pages.has(pageNumber)) return;

    const pendingRequest = this.#pageRequests.get(pageNumber);
    if (pendingRequest) {
      await pendingRequest;
      return;
    }

    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;

    const requestGeneration = this.#cacheGeneration;
    const request = (async () => {
      const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
      if (requestGeneration !== this.#cacheGeneration) return;

      this.pages.set(pageNumber, pageContent);

      const parsedPage = new DOMParser().parseFromString(pageContent, 'text/html');
      const gridElement = parsedPage.querySelector('[ref="grid"]');
      if (gridElement) {
        this.pageItems.set(pageNumber, Array.from(gridElement.querySelectorAll(':scope > [ref="cards[]"]')));
      }
    })();

    this.#pageRequests.set(pageNumber, request);

    try {
      await request;
    } finally {
      if (this.#pageRequests.get(pageNumber) === request) {
        this.#pageRequests.delete(pageNumber);
      }
    }
  }

  /**
   * Loads one progressive batch after automatic loading reaches its cap.
   */
  async loadMore() {
    const { loadMoreButton } = this.refs;
    if (!loadMoreButton || this.#isRenderingNextPage) return;

    const defaultLabel = loadMoreButton.textContent;
    loadMoreButton.disabled = true;
    loadMoreButton.setAttribute('aria-busy', 'true');
    loadMoreButton.textContent = 'Cargando…';

    try {
      await this.#renderNextPage(true);
    } finally {
      loadMoreButton.disabled = false;
      loadMoreButton.removeAttribute('aria-busy');
      loadMoreButton.textContent = defaultLabel;
      this.#updateLoadMoreButton();
    }
  }

  /**
   * @param {boolean} manual - Whether loading was explicitly requested by the customer.
   */
  async #renderNextPage(manual = false) {
    const { grid } = this.refs;

    if (!grid || this.#isRenderingNextPage) return;

    if (!manual && this.#shouldPauseAutoLoading()) {
      this.#updateLoadMoreButton();
      return;
    }

    this.#isRenderingNextPage = true;

    try {
      const queuedPage = this.#nextPageInfo;
      const nextPage = queuedPage || this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage)) return;
      let nextPageItemElements = queuedPage ? this.#nextPageItems : Array.from(this.#getGridForPage(nextPage.page) || []);

      if (!nextPageItemElements.length) {
        this.#scheduleSkeleton(false);
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = Array.from(this.#getGridForPage(nextPage.page) || []);
        if (!nextPageItemElements.length) return;
      }

      const configuredBatchSize = Number(this.getAttribute('load-batch-size'));
      const batchSize = Number.isFinite(configuredBatchSize) && configuredBatchSize > 0
        ? Math.floor(configuredBatchSize)
        : nextPageItemElements.length;
      const itemsToAppend = nextPageItemElements.slice(0, batchSize);
      const remainingItems = nextPageItemElements.slice(batchSize);

      grid.append(...itemsToAppend);

      this.#aspectRatioHelper?.processNewElements();

      await yieldToMainThread();

      // Keep the canonical collection URL stable while more cards are appended.
      // Updating the address here made a normal scroll appear to jump to page 2/3.

      if (remainingItems.length) {
        this.#nextPageItems = remainingItems;
        this.#nextPageInfo = nextPage;
      } else {
        this.#nextPageItems = [];
        this.#nextPageInfo = null;
        this.pages.delete(nextPage.page);
        this.pageItems.delete(nextPage.page);
      }
    } finally {
      this.#hideSkeleton();
      this.#isRenderingNextPage = false;
      this.#updateLoadMoreButton();
    }
  }

  /**
   * Delay the loading state slightly so a fast cached response never flashes.
   * @param {boolean} replaceCards - Hide current results while filters are being applied.
   */
  #scheduleSkeleton(replaceCards) {
    this.#hideSkeleton();
    this.#replaceCardsWithSkeleton = replaceCards;
    this.#skeletonTimer = setTimeout(() => this.#showSkeleton(), 160);
  }

  #showSkeleton() {
    const { grid } = this.refs;
    if (!grid || grid.querySelector('[data-mjr-product-skeleton]')) return;

    const configuredBatchSize = Number(this.getAttribute('load-batch-size'));
    const skeletonCount = Math.min(
      4,
      Math.max(1, Number.isFinite(configuredBatchSize) && configuredBatchSize > 0 ? configuredBatchSize : 4)
    );
    const fragment = document.createDocumentFragment();

    for (let index = 0; index < skeletonCount; index += 1) {
      const card = document.createElement('li');
      card.className = 'product-grid__item product-grid__loading-card';
      card.dataset.mjrProductSkeleton = '';
      card.setAttribute('aria-hidden', 'true');
      card.innerHTML = `
        <span class="product-grid__loading-media"></span>
        <span class="product-grid__loading-content">
          <span class="product-grid__loading-line product-grid__loading-line--title"></span>
          <span class="product-grid__loading-line product-grid__loading-line--text"></span>
          <span class="product-grid__loading-line product-grid__loading-line--price"></span>
        </span>
      `;
      fragment.append(card);
    }

    grid.dataset.mjrLoading = this.#replaceCardsWithSkeleton ? 'replace' : 'append';
    grid.append(fragment);
    grid.closest('#ResultsList')?.setAttribute('aria-busy', 'true');
  }

  #hideSkeleton() {
    if (this.#skeletonTimer) {
      clearTimeout(this.#skeletonTimer);
      this.#skeletonTimer = undefined;
    }

    const { grid } = this.refs;
    if (!grid) return;

    grid.querySelectorAll('[data-mjr-product-skeleton]').forEach((element) => element.remove());
    delete grid.dataset.mjrLoading;
    grid.closest('#ResultsList')?.removeAttribute('aria-busy');
    this.#replaceCardsWithSkeleton = false;
  }

  #shouldPauseAutoLoading() {
    const { grid } = this.refs;
    const configuredLimit = Number(this.getAttribute('auto-load-product-limit'));
    const renderedProductCount = grid?.querySelectorAll(':scope > [ref="cards[]"]').length || 0;

    return Boolean(
      grid &&
      Number.isFinite(configuredLimit) &&
      configuredLimit > 0 &&
      renderedProductCount >= configuredLimit
    );
  }

  #updateLoadMoreButton() {
    const { loadMoreButton } = this.refs;
    if (!loadMoreButton) return;

    const nextPage = this.#nextPageInfo || this.#getPage('next');
    loadMoreButton.hidden = !(this.#shouldPauseAutoLoading() && this.#shouldUsePage(nextPage));
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    // Preserve the collection URL when earlier cards are restored above the viewport.

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { grid } = this.refs;
    const isPrevious = type === 'previous';

    // Component refs are collected when the section mounts and can become
    // stale after products are appended through pagination. Always read the
    // current grid so the last rendered page, and therefore the next page,
    // are calculated correctly.
    const cards = grid ? Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]')) : [];

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const cachedItems = this.pageItems.get(page);
    if (cachedItems) return cachedItems;

    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    const items = Array.from(gridElement.querySelectorAll(':scope > [ref="cards[]"]'));
    this.pageItems.set(page, items);
    return items;
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.#cacheGeneration += 1;
    this.pages.clear();
    this.pageItems.clear();
    this.#pageRequests.clear();
    this.#nextPageItems = [];
    this.#nextPageInfo = null;
    this.#isRenderingNextPage = false;
    this.#updateLoadMoreButton();
    this.#scheduleSkeleton(true);

    const updatePromise = /** @type {{ promise?: Promise<unknown> }} */ (event).promise;
    updatePromise?.finally(() => this.#hideSkeleton()).catch(() => {});

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect either changed products or pagination.
    // A filter can replace the products while keeping the same last-page count.
    let updateHandled = false;
    /** @type {MutationObserver | undefined} */
    let observer;
    const prepareFilteredNextPage = () => {
      if (updateHandled) return;
      updateHandled = true;
      observer?.disconnect();

      if (!this.isConnected) return;

      this.#hideSkeleton();
      this.#observeViewMore();
      this.#fetchPage('next');
      this.#updateLoadMoreButton();
    };

    observer = new MutationObserver((mutations) => {
      const gridUpdated = mutations.some((mutation) => {
        if (mutation.attributeName === 'data-last-page') return true;
        if (mutation.type !== 'childList') return false;

        // Do not mistake our own temporary placeholders for rendered results.
        return [...mutation.addedNodes, ...mutation.removedNodes].some(
          (node) => !(node instanceof Element && node.matches('[data-mjr-product-skeleton]'))
        );
      });

      if (gridUpdated) prepareFilteredNextPage();
    });

    // Observe the grid for changes
    if (this.isConnected) {
      observer.observe(this, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
        subtree: true,
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        prepareFilteredNextPage();
      }, 3000);
    }
  };
}
